import React, { useEffect, useState } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
} from '@mui/material';
import {
  Assignment,
  AccessTime,
  FolderOpen,
  Business,
  WorkOutline,
  ViewKanban,
  Dashboard as DashboardIcon,
  BeachAccess,
  DirectionsCar,
  Timeline,
} from '@mui/icons-material';
import TopWorkersWidget from './TopWorkersWidget';
import { permissionService, Permissions } from '../services/permissionService';
import logoImage from '../assets/KIPTERV_LOGO.jpg';

const DRAWER_WIDTH = 240;
const ESTIMATED_APP_VERSION = '5.11.6.14';

interface SidebarProps {
  selectedMenu: string | null;
  setSelectedMenu: (menu: string) => void;
}

const menuItems = [
  { 
    text: 'Vezérlőpult', 
    icon: <DashboardIcon />, 
    key: 'dashboard',
    permission: Permissions.ViewDashboard 
  },
  { 
    text: 'Ajánlatok', 
    icon: <Assignment />, 
    key: 'ajanlatok',
    permission: Permissions.ViewOffers 
  },
  { 
    text: 'Időráfordítások', 
    icon: <AccessTime />, 
    key: 'idoraforditasok',
    permission: Permissions.ViewTimeTracking 
  },
  { 
    text: 'Iktatások', 
    icon: <FolderOpen />, 
    key: 'iktatas',
    permission: Permissions.ViewRegistrations 
  },
  { 
    text: 'Megrendelők', 
    icon: <Business />, 
    key: 'megrendelok',
    permission: Permissions.ViewClients 
  },
  { 
    text: 'Munkaszámok', 
    icon: <WorkOutline />, 
    key: 'munkaszamok',
    permission: Permissions.ViewWorkNumbers 
  },
  {
    text: '  Szabadságok',
    icon: <BeachAccess />,
    key: 'szabadsagok',
    permission: Permissions.ViewLeaves
  },
  {
    text: '  Céges autó',
    icon: <DirectionsCar />,
    key: 'cegesauto',
    permission: Permissions.ViewCompanyCar
  },
  {
    text: 'Task Manager',
    icon: <ViewKanban />,
    key: 'taskmanager',
    permission: Permissions.ViewWorkNumbers
  },
  {
    text: 'Projekttervezés',
    icon: <Timeline />,
    key: 'projekttervezes',
    permission: Permissions.ViewProjectPlanning
  },
];

const Sidebar: React.FC<SidebarProps> = ({ selectedMenu, setSelectedMenu }) => {
  const [visibleMenuItems, setVisibleMenuItems] = useState(menuItems);

  useEffect(() => {
    // Filter menu items based on permissions
    const filterMenuItems = () => {
      const filtered = menuItems.filter(item => 
        permissionService.hasPermission(item.permission)
      );
      setVisibleMenuItems(filtered);
    };

    // Initial filter
    filterMenuItems();

    // Subscribe to permission changes
    const unsubscribe = permissionService.onPermissionsChanged(() => {
      filterMenuItems();
    });

    // Cleanup subscription
    return () => {
      unsubscribe();
    };
  }, []);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: '#3a4651', // dark blue-gray
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            px: 2.6,
            py: 0.8,
            borderRadius: '999px',
            backgroundColor: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={logoImage}
            alt="KipNet logó"
            sx={{ maxWidth: '100%', height: 40, width: 'auto' }}
          />
        </Box>
      </Toolbar>
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <List>
          {visibleMenuItems.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton
                selected={selectedMenu === item.key}
                onClick={() => setSelectedMenu(item.key)}
                sx={{
                  color: 'white',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
      <Box sx={{ mt: 'auto', mb: 2 }}>
        <TopWorkersWidget />
        <Box sx={{ px: 2, mt: 1.25 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.48)',
              letterSpacing: 0.2,
            }}
          >
            v{ESTIMATED_APP_VERSION}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
